import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save,
  Calendar,
  DollarSign,
  FileText,
  Clock,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import CloudinaryUpload from '../../components/ui/CloudinaryUpload';
import AdminClassVideos from '../../components/AdminClassVideos';
import AdminClassCalendar from '../../components/AdminClassCalendar';
import { toast } from 'sonner';
import api from '../../lib/api';

const AdminSpecialCourseEdit = () => {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    long_description: '',
    hero_image_url: '',
    // Pricing
    price_online: '',
    price_online_installments: '',
    price_presencial: '',
    price_presencial_installments: '',
    price_pack: '',
    price_pack_installments: '',
    deposit_amount: '',
    // Dates
    start_date: '',
    end_date: '',
    enrollment_deadline: '',
    // Structure
    online_sessions: '',
    presencial_weekends: '',
    payment_day: ''
  });

  useEffect(() => {
    if (slug) {
      fetchCourse();
    }
  }, [slug]);

  const fetchCourse = async () => {
    try {
      const response = await api.get(`/special-courses/${slug}`);
      setCourse(response.data);
      setFormData({
        title: response.data.title || '',
        subtitle: response.data.subtitle || '',
        description: response.data.description || '',
        long_description: response.data.long_description || '',
        hero_image_url: response.data.hero_image_url || '',
        price_online: response.data.price_online || '',
        price_online_installments: response.data.price_online_installments || '',
        price_presencial: response.data.price_presencial || '',
        price_presencial_installments: response.data.price_presencial_installments || '',
        price_pack: response.data.price_pack || '',
        price_pack_installments: response.data.price_pack_installments || '',
        deposit_amount: response.data.deposit_amount || '',
        start_date: response.data.start_date || '',
        end_date: response.data.end_date || '',
        enrollment_deadline: response.data.enrollment_deadline || '',
        online_sessions: response.data.online_sessions || '',
        presencial_weekends: response.data.presencial_weekends || '',
        payment_day: response.data.payment_day || ''
      });
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar el curso');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/special-courses/${slug}`, formData);
      toast.success('Curso actualizado correctamente');
    } catch (error) {
      toast.error('Error al guardar los cambios');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[hsl(var(--accent))] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-[hsl(var(--text-secondary))]">Curso no encontrado</p>
          <Link to="/admin/formaciones" className="text-[hsl(var(--accent))] hover:underline mt-2 inline-block">
            Volver a formaciones
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[hsl(var(--sage-light))]">
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link 
              to="/admin/formaciones" 
              className="inline-flex items-center gap-2 text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))] mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
              Volver a formaciones
            </Link>
            <h1 className="text-2xl font-heading text-[hsl(var(--text-primary))]">
              Editar: {course.title}
            </h1>
            <p className="text-sm text-[hsl(var(--text-tertiary))] mt-1">
              Slug: {slug}
            </p>
          </div>
          
          <Button 
            onClick={handleSave} 
            className="btn-gold"
            disabled={saving}
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Guardar cambios
          </Button>
        </div>

        <div className="space-y-8">
          {/* Información básica */}
          <div className="card-soft">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-5 h-5 text-[hsl(var(--accent))]" />
              <h2 className="text-lg font-heading text-[hsl(var(--text-primary))]">
                Información básica
              </h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
                  Título del curso
                </label>
                <Input
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Nombre del curso"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
                  Subtítulo
                </label>
                <Input
                  name="subtitle"
                  value={formData.subtitle}
                  onChange={handleChange}
                  placeholder="Ej: 3ª Edición · Marzo 2026 – Junio 2026"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
                  Descripción corta (para preview)
                </label>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Descripción breve del curso..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
                  Descripción larga (para landing)
                </label>
                <Textarea
                  name="long_description"
                  value={formData.long_description}
                  onChange={handleChange}
                  rows={6}
                  placeholder="Descripción completa del curso..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
                  Imagen principal
                </label>
                <CloudinaryUpload
                  value={formData.hero_image_url}
                  onChange={(url) => setFormData(prev => ({ ...prev, hero_image_url: url }))}
                  folder="escuela/cursos"
                  aspectRatio="video"
                  placeholder="Sube la imagen principal del curso"
                />
                <p className="text-xs text-[hsl(var(--text-tertiary))] mt-2">
                  O introduce una URL externa:
                </p>
                <Input
                  name="hero_image_url"
                  value={formData.hero_image_url}
                  onChange={handleChange}
                  placeholder="https://..."
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Precios */}
          <div className="card-soft">
            <div className="flex items-center gap-3 mb-6">
              <DollarSign className="w-5 h-5 text-[hsl(var(--accent))]" />
              <h2 className="text-lg font-heading text-[hsl(var(--text-primary))]">
                Precios
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Online */}
              <div className="p-4 rounded-xl bg-[hsl(var(--sage-light))]">
                <h3 className="font-medium text-[hsl(var(--text-primary))] mb-4">Online</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-[hsl(var(--text-tertiary))] mb-1">Pronto pago (€)</label>
                    <Input
                      name="price_online"
                      type="number"
                      value={formData.price_online}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[hsl(var(--text-tertiary))] mb-1">Total cuotas (€)</label>
                    <Input
                      name="price_online_installments"
                      type="number"
                      value={formData.price_online_installments}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              {/* Presencial */}
              <div className="p-4 rounded-xl bg-[hsl(var(--sage-light))]">
                <h3 className="font-medium text-[hsl(var(--text-primary))] mb-4">Presencial</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-[hsl(var(--text-tertiary))] mb-1">Pronto pago (€)</label>
                    <Input
                      name="price_presencial"
                      type="number"
                      value={formData.price_presencial}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[hsl(var(--text-tertiary))] mb-1">Total cuotas (€)</label>
                    <Input
                      name="price_presencial_installments"
                      type="number"
                      value={formData.price_presencial_installments}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              {/* Pack */}
              <div className="p-4 rounded-xl bg-[hsl(var(--sage-light))]">
                <h3 className="font-medium text-[hsl(var(--text-primary))] mb-4">Pack Completo</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-[hsl(var(--text-tertiary))] mb-1">Pronto pago (€)</label>
                    <Input
                      name="price_pack"
                      type="number"
                      value={formData.price_pack}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[hsl(var(--text-tertiary))] mb-1">Total cuotas (€)</label>
                    <Input
                      name="price_pack_installments"
                      type="number"
                      value={formData.price_pack_installments}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
                Señal / Paga y señal (€)
              </label>
              <Input
                name="deposit_amount"
                type="number"
                value={formData.deposit_amount}
                onChange={handleChange}
                className="max-w-xs"
              />
            </div>
          </div>

          {/* Fechas */}
          <div className="card-soft">
            <div className="flex items-center gap-3 mb-6">
              <Calendar className="w-5 h-5 text-[hsl(var(--accent))]" />
              <h2 className="text-lg font-heading text-[hsl(var(--text-primary))]">
                Fechas
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
                  Fecha de inicio
                </label>
                <Input
                  name="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={handleChange}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
                  Fecha de fin
                </label>
                <Input
                  name="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={handleChange}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
                  Límite de inscripción
                </label>
                <Input
                  name="enrollment_deadline"
                  type="date"
                  value={formData.enrollment_deadline}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* Estructura */}
          <div className="card-soft">
            <div className="flex items-center gap-3 mb-6">
              <Clock className="w-5 h-5 text-[hsl(var(--accent))]" />
              <h2 className="text-lg font-heading text-[hsl(var(--text-primary))]">
                Estructura del curso
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
                  Sesiones online
                </label>
                <Input
                  name="online_sessions"
                  type="number"
                  value={formData.online_sessions}
                  onChange={handleChange}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
                  Fines de semana presenciales
                </label>
                <Input
                  name="presencial_weekends"
                  type="number"
                  value={formData.presencial_weekends}
                  onChange={handleChange}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
                  Día de pago (del mes)
                </label>
                <Input
                  name="payment_day"
                  type="number"
                  min="1"
                  max="28"
                  value={formData.payment_day}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* Clases en Vídeo */}
          <AdminClassVideos courseSlug={slug} />

          {/* Calendario de Clases */}
          <AdminClassCalendar courseSlug={slug} courseId={course?.id} />

          {/* Links rápidos */}
          <div className="card-soft bg-[hsl(var(--sage))]/10">
            <h2 className="text-lg font-heading text-[hsl(var(--text-primary))] mb-4">
              Enlaces rápidos
            </h2>
            <div className="flex flex-wrap gap-4">
              <Link to={`/admin/formaciones/${slug}/modulos`}>
                <Button variant="outline">
                  Gestionar contenido de módulos
                </Button>
              </Link>
              <Link to={`/admin/formaciones/${slug}/inscripciones`}>
                <Button variant="outline">
                  Ver inscripciones
                </Button>
              </Link>
              <a 
                href={`/formaciones/${slug}`} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button variant="outline">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Ver landing pública
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSpecialCourseEdit;
